import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import Divider from '../../../components/ui/Divider';
import InputField from '../../../components/forms/form-elements/InputField';
import Button from '../../../components/buttons/Button';
import { ReactComponent as Pencil } from '../../../icons/svg/ci_edit.svg';
import { useAuth } from '../../../hooks/useAuth';
import { profileSchema } from '../../../validation/schemas/profile';
import { toBase64 } from '../../../utils/files';
import { deleteAccount, updateOwnerDetails } from '../../../api/owner';
import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';
import { getTimeAgoText } from '../../../utils/dates';
import { Dialog } from '@headlessui/react';
import CustomRadioGroup from '../../../components/forms/form-elements/CustomRadioGroup';
import LoadingBall from '../../../components/loading/LoadingBall';

interface IFormInput {
  name: string;
  email: string;
  phone: string;
  password?: string;
  image?: string;
}

interface IReasonInput {
  reason: string;
}

const EditProfileTab = () => {
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);

  const changeImgRef = useRef<HTMLInputElement>(null);

  const { user, updateUser, logoutUser } = useAuth();

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<IFormInput>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      password: '',
    },
    resolver: yupResolver(profileSchema),
    mode: 'all',
  });

  const {
    register: registerReasonForm,
    setValue,
    setFocus,
    getValues,
    formState: { isValid: isReasonValid },
  } = useForm<IReasonInput>({
    defaultValues: {
      reason: '',
    },
  });

  const firstName = useMemo(() => user?.name.split(' ')[0] || '', [user]);

  const lastName = useMemo(() => user?.name.split(' ')[1] || '', [user]);

  const imageUrl = useMemo(() => user?.imageUrl || null, [user]);

  const email = useMemo(() => {
    if (user) {
      const [emailFirstPart, emailSecondPart] = user.email.split('@');
      const hiddenFirstPart = emailFirstPart.substring(0, 2) + '***';

      return `${hiddenFirstPart}@${emailSecondPart}`;
    }

    return '';
  }, [user]);

  const phone = useMemo(() => {
    if (user && user.phone) {
      const userPhone = user.phone;
      const phoneFirstPart = userPhone.substring(0, 2);
      const phoneLastPart = userPhone.substring(userPhone.length - 3);

      return `${phoneFirstPart}****${phoneLastPart}`;
    }

    return '';
  }, [user]);

  const password = useMemo(() => {
    const passLastChanged = user?.passwordLastUpdated;

    if (passLastChanged) {
      return `${t(COMMON_TRANSLATE_KEYS.UPDATED_PASSWORD)} ${getTimeAgoText(
        passLastChanged,
        true
      )}`;
    }

    return t(COMMON_TRANSLATE_KEYS.NO_PASSWORD_SET);
  }, [user?.passwordLastUpdated, t]);

  const DeleteSuggestions = useMemo(() => {
    return [
      {
        id: 1,
        name: t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_REASON_1),
      },
      {
        id: 2,
        name: t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_REASON_2),
      },
      {
        id: 3,
        name: t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_REASON_3),
      },
      {
        id: 4,
        name: t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_REASON_4),
      },
    ];
  }, [t]);

  const toggleForm = () => {
    setShowForm((prev) => !prev);
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (files && files.length > 0) {
      const imageFile = files[0];

      const base64Image = await toBase64(imageFile);

      const { data, error } = await updateOwnerDetails({
        image: base64Image,
      });

      if (error) {
        toast(error.error.message, {
          toastId: 'image-upload-error',
        });
        return;
      }

      if (data) {
        updateUser(data);
      }
    }
  };

  const handleFormSubmit = async (formData: IFormInput) => {
    const {
      name: changedName,
      phone: changedPhone,
      email: changedEmail,
      password: changedPass,
    } = formData;

    const payload = {
      name: changedName,
      phone: changedPhone,
      imageUrl: user?.imageUrl,
      email: changedEmail,
      password: changedPass || undefined,
    };

    const { data, error } = await updateOwnerDetails(payload);

    if (error) {
      toast(error.error.message || error.error, {
        toastId: 'update-owner',
      });
      return;
    }

    if (data) {
      updateUser(data);
      toggleForm();
      reset({
        name: data.name,
        phone: data.phone,
        email: data.email,
      });
    }
  };

  const closeModal = useCallback((dontReset?: boolean) => {
    setSelectedReason(null);
    setShowModal(false);

    if (!dontReset) {
      setValue('reason', '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [setValue]);

  const handleDeleteProfile = useCallback(async () => {
    closeModal(true);
    setLoadingModal(true);

    const payload = {
      reason: getValues('reason'),
    };

    const { data, error } = await deleteAccount(payload, t);

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'delete-profile-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      setLoadingModal(false);

      return;
    }

    if (data) {
      logoutUser();
    }

    setLoadingModal(false);
  }, [closeModal, getValues, t, logoutUser]);

  const onSelectDeclineReason = useCallback(
    (value: string | ((prevState: string | null) => string | null) | null) => {
      setSelectedReason(value);

      if (value) {
        setValue('reason', value as string, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const renderUserDetailsTableContent = useCallback(() => {
    const tableData = [
      {
        label: t(COMMON_TRANSLATE_KEYS.FIRST_NAME),
        value: firstName,
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.LAST_NAME),
        value: lastName,
      },
      {
        label: 'Divider', // Used only to divide the form fields
        value: 'divider',
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.PASSWORD),
        value: password,
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.EMAIL),
        value: email,
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.PHONE_NUMBER),
        value: phone,
      },
    ];

    return tableData.map(({ label, value }, i) =>
      i === 2 ? (
        <tr key={`${label} ${value}`}>
          <td colSpan={3}>
            <Divider mb={4} mt={4} color="bg-gray-700" width={'100%'} />
          </td>
        </tr>
      ) : (
        <tr key={`${label} ${value}`}>
          <td className="py-3">
            <p>{label}</p>
          </td>
          <td className="py-3 text-ellipsis max-w-24">
            <p className="text-ellipsis overflow-hidden">{value}</p>
          </td>
          <td className="py-2 flex justify-end">
            <button className="text-mainBlue" onClick={() => toggleForm()}>
              {t(COMMON_TRANSLATE_KEYS.CHANGE)}
            </button>
          </td>
        </tr>
      )
    );
  }, [t, firstName, lastName, password, email, phone]);

  const renderProfilePicture = useCallback(() => {
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt="profile"
          className="rounded-full border border-gray-400 w-[100px] h-[100px] object-cover"
          width={36}
          height={36}
        />
      );
    }

    return (
      <div className="rounded-full border border-gray-400 w-[100px] h-[100px] bg-mainBlack"></div>
    );
  }, [imageUrl]);

  const renderForm = useCallback(() => {
    const tableData: {
      label: string;
      value: keyof IFormInput;
      disabled?: boolean;
      type?: string;
      autoComplete?: string;
      isRequired?: boolean;
    }[] = [
      {
        label: t(COMMON_TRANSLATE_KEYS.NAME),
        value: 'name',
        isRequired: true,
      },
      {
        label: 'Divider', // Used only for dividing the form elements
        value: 'name',
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.PASSWORD),
        value: 'password',
        type: 'password',
        autoComplete: 'new-password',
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.EMAIL),
        value: 'email',
        isRequired: true,
      },
      {
        label: t(COMMON_TRANSLATE_KEYS.PHONE),
        value: 'phone',
        isRequired: true,
      },
    ];

    return tableData.map(({ label, value, isRequired, ...rest }, i) =>
      i === 1 ? (
        <tr key={`${label} ${value}`}>
          <td colSpan={3}>
            <Divider mb={4} mt={4} color="bg-gray-700" width={'100%'} />
          </td>
        </tr>
      ) : (
        <tr key={`${label} ${value}`}>
          <td className="py-3">
            <p>
              {label}{' '}
              {isRequired ? <span className="text-orange-400">*</span> : null}
            </p>
          </td>
          <td colSpan={2} className="py-3">
            <InputField
              {...register(value)}
              error={t(errors[value]?.message || '')}
              {...rest}
            />
          </td>
        </tr>
      )
    );
  }, [register, errors, t]);

  useEffect(() => {
    if (showModal) {
      setFocus('reason', { shouldSelect: true });
    }
  }, [showModal, setFocus]);

  return (
    <>
      <div className="lg:min-w-[440px] lg:w-2/5 flex flex-col gap-8">
        <div className="flex justify-center">
          <div
            className="rounded-full relative"
            onClick={() => changeImgRef.current?.click()}
          >
            {renderProfilePicture()}
            <Pencil
              className="absolute right-0 top-1/2 cursor-pointer"
              style={{ transform: 'translate(-15%, 100%)' }}
            />
            <input
              ref={changeImgRef}
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={async (e) => await handleImageUpload(e.target.files)}
            />
          </div>
        </div>
        <table>
          <tbody>
            {showForm ? renderForm() : renderUserDetailsTableContent()}
          </tbody>
        </table>
        {showForm && (
          <div className="flex gap-3">
            <Button
              backgroundColor="bg-mainGreen"
              textColor="text-black-300"
              isDisabled={!isValid}
              onClick={handleSubmit(handleFormSubmit)}
            >
              {t(COMMON_TRANSLATE_KEYS.SAVE)}
            </Button>
            <Button
              backgroundColor="bg-cancel"
              textColor="text-black-300"
              onClick={() => {
                reset();
                toggleForm();
              }}
            >
              {t(COMMON_TRANSLATE_KEYS.CANCEL)}
            </Button>
          </div>
        )}
        {!showForm && (
          <div className="flex gap-3">
            <Button
              backgroundColor="bg-cancel"
              textColor="text-black-300"
              onClick={() => setShowModal(true)}
            >
              {t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT)}
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={loadingModal}
        onClose={() => undefined}
        className="fixed inset-0 flex flex-col items-center justify-center z-50"
      >
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />
        <div className="rounded-xl flex-col items-center justify-center gap-10 p-10">
          <LoadingBall />
          <p className="text-white text-2xl p-3 mt-10 rounded-xl bg-black">
            {t(COMMON_TRANSLATE_KEYS.DELETE_PROFILE_LOADING_TEXT)}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={showModal}
        onClose={() => closeModal()}
        className="fixed inset-0 flex items-center justify-center z-50"
      >
        <Dialog.Overlay className="fixed inset-0 bg-black  opacity-50" />
        <div className="bg-additionalBlack p-6 rounded-xl absolute top-212 left-468 lg:max-w-[40%] mx-6 h-[70%] overflow-auto">
          <div className="flex flex-row justify-between mb-3 items-start">
            <Dialog.Title className="text-white font-bold text-xl">
              {t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_MODAL_TITLE)}
            </Dialog.Title>
            <button
              className="ps-3 pb-3 text-white"
              onClick={() => closeModal()}
            >
              X
            </button>
          </div>
          <div className="w-full">
            <p className="text-textGray">
              {t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_MODAL_TEXT)}
            </p>
          </div>
          <div className="w-full">
            <p className="text-white text-sm mt-6 mb-4">
              {t(COMMON_TRANSLATE_KEYS.DELETE_ACCOUNT_REASON)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
          </div>
          <div className="mb-6">
            <InputField
              {...registerReasonForm('reason', { required: true })}
              placeholder={t(
                COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_PLACEHOLDER
              )}
              height={3}
            />
          </div>
          <CustomRadioGroup
            items={DeleteSuggestions}
            value={selectedReason}
            onChange={onSelectDeclineReason}
            className="flex flex-col gap-3 my-3"
          />
          <div className="flex justify-center gap-3 mt-5">
            <Button
              textColor="text-white"
              backgroundColor="bg-functionalRed"
              height={3}
              onClick={async () => {
                handleDeleteProfile();
              }}
              isDisabled={!isReasonValid}
            >
              {t(COMMON_TRANSLATE_KEYS.DELETE)}
            </Button>
            <Button
              textColor="text-white"
              backgroundColor="bg-mainBlue"
              height={3}
              onClick={closeModal}
            >
              {t(COMMON_TRANSLATE_KEYS.CANCEL)}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EditProfileTab;
